import { ref, onUnmounted, type Ref } from "vue"

/**
 * 使用安全的 setTimeout 鏈來防止請求重疊。
 * @param fn - 要執行的異步函數，回傳 true 時停止輪詢。
 * @param interval - 輪詢間隔時間，預設為 5000 毫秒。
 * @returns 返回響應式狀態和控制函數。
 */
export function usePolling(
    fn: (...args: unknown[]) => Promise<boolean | void>,
    interval = 5000
): {
    isPolling: Ref<boolean>
    startPolling: (...args: unknown[]) => void
    stopPolling: () => void
} {
    const isPolling = ref(false)
    const isExecuting = ref(false)
    let timerId: ReturnType<typeof setTimeout> | null = null

    // core function
    const poll = async (...args: unknown[]): Promise<void> => {
        // 防止重疊執行
        if (isExecuting.value) return

        isExecuting.value = true
        const shouldStop = await fn(...args)
        isExecuting.value = false

        if (shouldStop || !isPolling.value) {
            stopPolling()
            return
        }

        timerId = setTimeout(() => poll(...args), interval)
    }

    // start polling
    const startPolling = (...args: unknown[]): void => {
        if (isPolling.value) return

        isPolling.value = true
        void poll(...args)
    }

    // stop polling
    const stopPolling = (): void => {
        isPolling.value = false
        if (timerId) {
            clearTimeout(timerId)
            timerId = null
        }
    }

    // avoid memory leak
    onUnmounted(stopPolling)

    return {
        isPolling,
        startPolling,
        stopPolling,
    }
}
